import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { CATALOGUE_METRIC_KEYS, CatalogueMetricKey } from '../helpers/catalogueUtils'
import { metricLensDescription, metricLensLabel } from '../helpers/metricLensCopy'
import { compactToggleGroupSx } from '../helpers/toggleStyles'

export type MetricLensToggleVariant =
  // Underlined tabs: visibly a different kind of control from the granularity,
  // window and view pills in the control row, which is the point (2026-09-07
  // review: the metric selector looked like one more filter).
  | 'tabs'
  // The former pill group. Kept behind this prop so the fallback is one line.
  | 'pills'

interface MetricLensToggleProps {
  value: CatalogueMetricKey
  onChange: (next: CatalogueMetricKey) => void
  variant?: MetricLensToggleVariant
}

// Tab styling: brand underline, bold label, no all-caps; compact enough to sit
// in a card header row next to sort columns.
const tabsSx = {
  minHeight: 36,
  '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
  '& .MuiTab-root': {
    minHeight: 36,
    minWidth: 0,
    px: 1.5,
    py: 0.5,
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '0.8125rem',
    color: 'text.secondary',
    '&.Mui-selected': { color: 'primary.main' },
  },
} as const

/**
 * The single "which number am I looking at" control of the Live Event
 * Analytics tool: one entry per `CATALOGUE_METRIC_KEYS` (Adoption | Event density).
 *
 * Rendered in the header of whichever card shows the metric (the ranked list,
 * the trend charts) rather than in a control row above them, so the chart or
 * column it changes is right beneath it. The definition of the selected metric
 * lives behind the help icon next to the chart title; the pills variant also
 * defines every lens on hover.
 */
const MetricLensToggleComponent: React.FC<MetricLensToggleProps> = ({ value, onChange, variant = 'tabs' }) => {
  const { t } = useTranslation()

  const handleToggleChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, next: CatalogueMetricKey | null) => {
      if (next !== null) onChange(next)
    },
    [onChange]
  )

  const handleTabChange = useCallback((_: React.SyntheticEvent, next: CatalogueMetricKey) => onChange(next), [onChange])

  // Hoisted so GRTooltip receives a stable element tree instead of a fresh one
  // on every render (only the active language changes the copy).
  const tooltip = useMemo(
    () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 360 }}>
        {CATALOGUE_METRIC_KEYS.map((metric) => (
          <Typography key={metric} variant="body2" component="p">
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              {metricLensLabel(metric, t)}:
            </Typography>{' '}
            {metricLensDescription(metric, t)}
          </Typography>
        ))}
      </Box>
    ),
    [t]
  )

  if (variant === 'tabs') {
    return (
      <Tabs
        value={value}
        onChange={handleTabChange}
        aria-label={t('market-analytics:catalogue_metric_label')}
        variant="scrollable"
        scrollButtons={false}
        sx={tabsSx}
      >
        {CATALOGUE_METRIC_KEYS.map((metric) => (
          <Tab key={metric} value={metric} label={metricLensLabel(metric, t)} />
        ))}
      </Tabs>
    )
  }

  // The buttons are direct children of the group on purpose: MUI's
  // `ToggleButtonGroup` injects the selection into them and a per-button
  // wrapper would break that, hence one tooltip for the whole group.
  return (
    <GRTooltip content={tooltip} styles={{ display: 'inline-flex' }}>
      <ToggleButtonGroup
        value={value}
        exclusive
        size="small"
        onChange={handleToggleChange}
        aria-label={t('market-analytics:catalogue_metric_label')}
        sx={compactToggleGroupSx}
      >
        {CATALOGUE_METRIC_KEYS.map((metric) => (
          <ToggleButton key={metric} value={metric}>
            {metricLensLabel(metric, t)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </GRTooltip>
  )
}

export const MetricLensToggle = React.memo(MetricLensToggleComponent)
MetricLensToggle.displayName = 'MetricLensToggle'
